import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Public page opened from the appointment reminder's "Confirm" URL button.
// Route: /confirm/:token — auto-confirms the appointment on load.

const API = process.env.REACT_APP_BACKEND_URL;

const BRAND = {
  cream: '#F5F2EA',
  charcoal: '#1A1A1A',
  slate: '#4A4A4A',
  gold: '#C5A059',
  goldSoft: 'rgba(197, 160, 89, 0.25)',
  emerald: '#059669',
};
const fontHead = { fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontWeight: 300, letterSpacing: '-0.01em', color: BRAND.charcoal };
const fontBody = { fontFamily: "'Manrope', 'Inter', system-ui, sans-serif", color: BRAND.charcoal };
const overlineStyle = { fontFamily: "'Manrope', system-ui, sans-serif", fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', color: BRAND.gold };

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch (_e) { return iso; }
};

const fmtTime = (t) => {
  if (!t) return null;
  try {
    const [h, m] = t.split(':');
    const hr = parseInt(h, 10);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
  } catch (_e) { return t; }
};

const AppointmentConfirmPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, err: null, data: null });
  const fired = useRef(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&family=Manrope:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    document.body.style.backgroundColor = BRAND.cream;
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    (async () => {
      try {
        const res = await axios.post(`${API}/api/public/appointment-confirm`, { token });
        setState({ loading: false, err: null, data: res.data });
      } catch (e) {
        setState({ loading: false, err: e.response?.data?.detail || 'Could not confirm your appointment', data: null });
      }
    })();
  }, [token]);

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: BRAND.cream, ...fontBody, padding: '96px 24px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }} data-testid="appointment-confirm-page">
        <div style={overlineStyle}>Eden Skin Clinic · Appointment</div>
        {children}
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: `1px solid ${BRAND.goldSoft}` }}>
          <p style={{ color: BRAND.slate, fontSize: 13 }}>
            Need to reschedule or anything else? Call our front desk at{' '}
            <a href="tel:+917710117777" style={{ color: BRAND.emerald, textDecoration: 'none', fontWeight: 600 }}>+91 77101 17777</a>
          </p>
        </div>
      </div>
    </div>
  );

  if (state.loading) {
    return wrap(<h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }}>Confirming your appointment…</h1>);
  }

  if (state.err) {
    return wrap(
      <>
        <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }} data-testid="confirm-error">Link unavailable</h1>
        <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>{state.err}</p>
      </>
    );
  }

  const d = state.data || {};
  const name = d.patient_first_name ? `, ${d.patient_first_name}` : '';
  const detail = (
    <div style={{ marginTop: 24, padding: 24, background: '#FFFFFF', border: `1px solid ${BRAND.goldSoft}` }}>
      <div style={overlineStyle}>Appointment details</div>
      <p style={{ fontSize: 17, lineHeight: 1.8, marginTop: 12, marginBottom: 0 }}>
        <strong>{d.service_name}</strong><br />
        {fmtDate(d.scheduled_date)}{fmtTime(d.scheduled_time) ? ` · ${fmtTime(d.scheduled_time)}` : ' · Walk-in queue (no fixed time)'}
      </p>
    </div>
  );

  if (d.outcome === 'confirmed') {
    return wrap(
      <>
        <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }} data-testid="confirm-success">Appointment confirmed{name}!</h1>
        <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>
          Thank you — we've marked your visit as confirmed. We look forward to seeing you.
        </p>
        {detail}
      </>
    );
  }

  if (d.outcome === 'already_confirmed') {
    return wrap(
      <>
        <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }} data-testid="confirm-already">Already confirmed</h1>
        <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>
          Your appointment is already confirmed{name}. Nothing more to do — see you soon!
        </p>
        {detail}
      </>
    );
  }

  if (d.outcome === 'already_visited') {
    return wrap(
      <>
        <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }}>Visit already recorded</h1>
        <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>
          Looks like this visit has already taken place. Thank you for coming in{name}!
        </p>
      </>
    );
  }

  return wrap(
    <>
      <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }} data-testid="confirm-not-possible">This appointment can't be confirmed</h1>
      <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>
        This appointment appears to have been {d.status === 'cancelled' ? 'cancelled' : 'changed'}.
        Please call our front desk and we'll sort it out right away.
      </p>
    </>
  );
};

export default AppointmentConfirmPage;
